import {EventData} from '@/types/event';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  RedditIcon,
  RedditShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon
} from 'react-share';
import {env} from '@/config';
import {useTranslation} from 'react-i18next';
import {Box, IconButton, Tooltip} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import {useCallback, useState} from 'react';
import {GroupData} from '@/types/group';
import {useAppSelector} from '@/redux-store/store';
import {getPublicCompanyTag} from '@/utils';

type DocumentType = 'event' | 'onDemandEvent' | 'group';
interface DocumentShareProps {
  document: EventData | GroupData;
  documentType: DocumentType;
}

const DocumentShare: React.FC<DocumentShareProps> = ({
  document,
  documentType
}) => {
  const {t} = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState(t('copy_link'));
  const {isPublicCompany, currentCompanyId} = useAppSelector(
    (state) => state.auth
  );
  const {
    SHARE_URL_GROUP,
    SHARE_URL_EVENT,
    ENVIRONMENT,
    APP_VARIANT,
    PUBLIC_COMPANIES
  } = env;
  const title = encodeURIComponent(document.title);

  const devExtension = ENVIRONMENT === 'production' ? '' : '&env=dev';
  let companyExtension = '';

  if (isPublicCompany && currentCompanyId) {
    const companyTag = getPublicCompanyTag(currentCompanyId, PUBLIC_COMPANIES);
    companyExtension = `&companyName=${companyTag}`;
  }

  const appVariant = APP_VARIANT === 'sjone' ? 'one - Active Team' : 'SportJoe';
  const appVariantExtension =
    APP_VARIANT === 'sjone' ? '&variant=sjone' : '&variant=sportjoe';

  const url =
    documentType === 'event'
      ? `${SHARE_URL_EVENT}${document._id}&t=${title}${devExtension}${companyExtension}${appVariantExtension}`
      : documentType === 'onDemandEvent'
      ? `${SHARE_URL_EVENT}${document._id}&t=${title}${devExtension}${companyExtension}${appVariantExtension}&type=on_demand`
      : `${SHARE_URL_GROUP}${document._id}&t=${title}${devExtension}${companyExtension}${appVariantExtension}`;

  const handleCopyLinkWith = useCallback(
    (url: string) => {
      navigator.clipboard.writeText(url).then(() => {
        setTooltipOpen(true);
        setTooltipTitle(t('link_copied'));
        setTimeout(() => {
          setTooltipOpen(false);
        }, 2000);
        setTimeout(() => {
          setTooltipTitle(t('copy_link'));
        }, 2100);
      });
    },
    [t]
  );

  const message = t('share_message', {
    appVariant
  });
  const messageWithUrl = t('share_message_with_url', {appVariant, url});

  return (
    <Box
      sx={{
        marginTop: 2,
        display: 'flex',
        gap: 1
      }}
    >
      <Tooltip
        title={t('share_with', {
          platform: 'E-mail'
        })}
      >
        <div>
          <EmailShareButton url={url} title={t('share_sj')} body={message}>
            <EmailIcon size={24} round={false} />
          </EmailShareButton>
        </div>
      </Tooltip>
      <Tooltip
        title={t('share_with', {
          platform: 'WhatsApp'
        })}
      >
        <a href={`whatsapp://send?text=${encodeURIComponent(messageWithUrl)}`}>
          <WhatsappIcon size={24} />
        </a>
      </Tooltip>
      <Tooltip
        title={t('share_with', {
          platform: 'Reddit'
        })}
      >
        <div>
          <RedditShareButton url={url} title={message}>
            <RedditIcon size={24} />
          </RedditShareButton>
        </div>
      </Tooltip>
      <Tooltip
        title={t('share_with', {
          platform: 'X'
        })}
      >
        <div>
          <TwitterShareButton url={url} title={message}>
            <TwitterIcon size={24} />
          </TwitterShareButton>
        </div>
      </Tooltip>
      <Tooltip
        title={t('share_with', {
          platform: 'LinkedIn'
        })}
      >
        <div>
          <LinkedinShareButton url={url} title={message}>
            <LinkedinIcon size={24} />
          </LinkedinShareButton>
        </div>
      </Tooltip>
      <Tooltip
        title={t('share_with', {
          platform: 'Facebook'
        })}
      >
        <div>
          <FacebookShareButton url={url} title={message}>
            <FacebookIcon size={24} />
          </FacebookShareButton>
        </div>
      </Tooltip>
      <Tooltip
        title={t('share_with', {
          platform: 'Telegram'
        })}
      >
        <div>
          <TelegramShareButton url={url} title={message}>
            <TelegramIcon size={24} />
          </TelegramShareButton>
        </div>
      </Tooltip>
      <Tooltip
        title={tooltipTitle}
        open={tooltipOpen}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        <div>
          <IconButton
            sx={{width: 24, height: 24}}
            onClick={() => handleCopyLinkWith(url)}
          >
            <LinkIcon sx={{fontSize: 24}} />
          </IconButton>
        </div>
      </Tooltip>
    </Box>
  );
};

export default DocumentShare;
